import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";

const GOOGLE_ERROR_MESSAGES = {
  google_not_configured: "Google login coming soon — use email/password for now.",
  oauth_state_mismatch: "Google sign-in session expired. Please try again.",
  oauth_state_invalid: "Google sign-in session invalid. Please try again.",
  oauth_exchange_failed: "Google sign-in failed. Please try again.",
  oauth_no_email: "Couldn't read your Google account email. Try email/password.",
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    const e = params.get("error");
    if (e && GOOGLE_ERROR_MESSAGES[e]) {
      toast(GOOGLE_ERROR_MESSAGES[e]);
      params.delete("error");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      // Phase 8b — tenants land on the tenant portal, not the landlord dashboard.
      let dest = location.state?.from?.pathname || "/dashboard";
      if (u?.role === "tenant") dest = "/tenant";
      navigate(dest, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Invalid email or password";
      setErr(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // Phase 36 Batch S — Emergent-managed Google Sign-In flow:
    //   1. Redirect to Emergent's hosted login page.
    //   2. User signs in with Google there.
    //   3. Emergent redirects back to /auth/callback with #session_id=... in
    //      the URL fragment, where AuthCallback exchanges it server-side
    //      and sets our httpOnly cookie.
    const redirect = encodeURIComponent(`${window.location.origin}/auth/callback`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2" data-testid="back-to-home">
          <img src="/logo.png" alt="runidea.ai" className="h-9 w-9 rounded-md object-cover" />
          <span className="text-base font-semibold tracking-tight text-slate-900">
            runidea.ai
          </span>
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="login-title">
            Welcome back
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Log in to your runidea workspace.
          </p>

          <Button
            type="button"
            variant="outline"
            onClick={onGoogle}
            data-testid="google-login-button"
            className="mt-6 w-full justify-center gap-2 border-slate-200 bg-white text-slate-700 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-sm"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.93-6.13-4.52H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.87 14.11c-.22-.66-.35-1.36-.35-2.11s.13-1.45.35-2.11V7.05H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.95l3.69-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.69 2.84C6.73 7.31 9.15 5.38 12 5.38z"
              />
            </svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-medium uppercase tracking-wider text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email" className="text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">
                  Password
                </Label>
                <button
                  type="button"
                  data-testid="login-forgot-password"
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 hover:underline"
                  onClick={() => setForgotOpen(true)}
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5"
              />
            </div>

            {err && (
              <p
                role="alert"
                data-testid="login-error"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {err}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            >
              {submitting ? "Signing in…" : "Log in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-semibold text-indigo-600 hover:underline"
              data-testid="link-to-signup"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
      <ForgotPasswordModal open={forgotOpen} onOpenChange={setForgotOpen} defaultEmail={email} />
    </div>
  );
}
